import React from 'react'
import { FaTimes } from 'react-icons/fa'
import styled, { css } from 'styled-components'

interface Props {
  header?: React.ReactNode
  headerIcon?: React.ReactNode
  footer?: React.ReactNode
  onClose: () => void
  children: React.ReactNode
  /** On narrow screens: title + close on row 1, headerIcon full-width on row 2. */
  stackHeaderOnMobile?: boolean
}

export const ModalContainer: React.FC<Props> = ({
  header = '',
  headerIcon,
  footer,
  onClose,
  children,
  stackHeaderOnMobile = false
}) => (
  <StyledContainer>
    <Header $stackOnMobile={stackHeaderOnMobile}>
      <HeaderTitle>{header}</HeaderTitle>
      {headerIcon && <HeaderIconSlot>{headerIcon}</HeaderIconSlot>}
      <CloseButton type="button" onClick={() => onClose()} aria-label="Close">
        <FaTimes size={32} />
      </CloseButton>
    </Header>
    <Content>{children}</Content>
    {footer && <Footer>{footer}</Footer>}
  </StyledContainer>
)

const StyledContainer = styled.div`
  height: 100%;
  width: 100%;
  min-height: 0;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  background: white;

  padding: ${({ theme }) => theme.spacing.xxs}rem;

  @media only screen and (min-width: 800px) {
    padding: ${({ theme }) => theme.spacing.sm}rem;
  }
`

const Content = styled.div`
  position: relative;
  flex: 1;
  min-height: 0;
  padding: ${({ theme }) => theme.spacing.sm}rem;
  overflow: auto;
`

const HeaderTitle = styled.h3`
  margin: 0;
  min-width: 0;
`

const HeaderIconSlot = styled.div`
  display: contents;
`

const CloseButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  margin: 0;
  padding: 0;
  border: none;
  background: transparent;
  cursor: pointer;
  color: inherit;
  font: inherit;

  svg {
    cursor: pointer;
    padding: 8px;
  }
`

const Header = styled.div<{ $stackOnMobile: boolean }>`
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${({ theme }) => theme.spacing.xs}rem;
  padding: ${({ theme }) => theme.spacing.sm}rem;

  ${HeaderTitle} {
    flex: 1;
    min-width: 0;
  }

  ${({ $stackOnMobile, theme }) =>
    $stackOnMobile &&
    css`
      @media (max-width: ${theme.breakpoint.md}) {
        flex-wrap: wrap;

        ${HeaderIconSlot} {
          display: block;
          flex-basis: 100%;
          order: 3;
          width: 100%;
          min-width: 0;
        }

        ${CloseButton} {
          order: 2;
        }

        ${HeaderTitle} {
          order: 1;
        }
      }
    `}
`

const Footer = styled.div`
  flex-shrink: 0;
  display: flex;
  justify-content: space-between;
  padding: ${({ theme }) => theme.spacing.sm}rem;
`
